import { useQuery, useMutation } from "@apollo/client/react"
import LoadingButton from "@mui/lab/LoadingButton"
import { Divider, Dialog, Tab, Tabs, Checkbox, MenuItem, FormHelperText } from "@mui/material"
import Alert from "@mui/material/Alert"
import IconButton from "@mui/material/IconButton"
import Paper from "@mui/material/Paper"
import { Formik, useFormikContext } from "formik"
import { AlertTriangle, Check, LoaderCircle, Maximize2, Minimize2, X } from "lucide-mui"
import { DateTime } from "luxon"
import React, { useEffect, useState, useMemo, useCallback } from "react"

import { TextField, DateField } from "@/components/form"
import PeopleAmountInput from "@/components/people_amount_input"
import { type VenuePlanData } from "@/components/plan"
import { STATUS_REMOVED, RESERVATION_COLORS, ReservationStatus } from "@/constants/reservations"
import { useUpdateReservationValidationSchema } from "@/forms/update_reservation"
import { UPDATE_RESERVATION_REFETCH_QUERIES } from "@/graphql/dependencies"
import { UpdateReservationDocument, GetReservationDocument, GetOpeningHoursDocument } from "@/graphql/graphql"
import i18n from "@/i18n"
import { createHoursList } from "@/shared/datetime"
import { formatTableName } from "@/shared/tables"
import { getOpeningHoursForDate } from "@/utils/opening_hours"

import CustomerAutocomplete from "../create_form/customer"
import RemoveButton from "../shared/remove_button"
import ReservationPlanSelector from "../shared/reservation_plan_selector"
import StatusSelector from "../shared/status_selector"

import ActivitiesTab from "./activities"
import NotesTab from "./notes"

const normalizePeopleAmount = (value: unknown): number | null => {
  if (value == null) return null
  if (typeof value === "string" && value.trim() === "") return null
  const n = Number(value)
  if (!Number.isFinite(n) || n <= 0) return null
  return n
}

const STYLES = {
  dialog: {
    paper: "max-w-5xl w-[calc(100%-64px)] h-[calc(100%-64px)]",
  },
  loading: {
    container: "flex items-center justify-center p-8",
    spinner: "animate-spin text-5xl text-gray-400",
  },
  error: {
    container: "inset-0 flex items-center justify-center",
    paper: "text-center p-4 flex flex-col items-center",
    icon: "block text-8xl text-red-600 mb-4",
    text: "text-red-600",
  },
  header: {
    base: "pl-4 pr-2 py-1 text-white text-sm flex flex-row space-between items-center",
    title: "flex-1",
    badge: "text-white text-xs px-2 py-1 ml-2 rounded bg-r-removed-light",
  },
  content: {
    main: "flex flex-col md:flex-row flex-1 min-h-0",
  },
  leftColumn: {
    container: "w-full md:w-1/3 bg-gray-100 p-4",
    customerSection: "space-y-3 mb-4",
    row: "grid grid-cols-2 gap-3",
    divider: "border-gray-400 my-4",
    reservationSection: "space-y-3",
    checkboxRow: "flex items-center text-sm text-gray-600",
  },
  rightColumn: {
    container: "w-full md:w-2/3 flex flex-col min-h-0",
    tabsContainer: "border-b border-gray-200 flex-shrink-0",
    tabContent: "flex-1 min-h-0 overflow-hidden",
    mapContainer: "h-full p-4",
  },
  footer: {
    container: "flex items-center justify-between px-6 py-3 border-t border-gray-300",
  },
}

interface EditFormContentProps {
  venue: any
  reservation: any
  venuePlans: VenuePlanData[]
  removed: boolean
  fullScreen: boolean
  onMapPendingChangesChange: (hasPendingChanges: boolean) => void
  reservationBuffer: number
  selectedCustomerContactId: string | null
  onSelectedCustomerContactIdChange: (id: string | null) => void
}

const EditFormContent: React.FC<EditFormContentProps> = ({
  venue,
  reservation,
  venuePlans,
  removed,
  fullScreen,
  onMapPendingChangesChange,
  reservationBuffer,
  selectedCustomerContactId,
  onSelectedCustomerContactIdChange,
}) => {
  const { values, setFieldValue } = useFormikContext<any>()
  const [selectedTab, setSelectedTab] = useState(0)

  const { data: openingHoursData } = useQuery(GetOpeningHoursDocument, {
    variables: { slug: venue.slug },
  })

  const { startHour, endHour } = useMemo(
    () => getOpeningHoursForDate(openingHoursData?.venue?.openingHours, values.date),
    [openingHoursData?.venue?.openingHours, values.date],
  )

  const timeOptions = useMemo(() => createHoursList(values.date, startHour, endHour), [values.date, startHour, endHour])

  const onTableSelect = (tableIds: string[]) => void setFieldValue("tableId", tableIds[0] || "")
  const onTimeSelect = (startTime: DateTime, endTime: DateTime) => {
    void setFieldValue("startTime", startTime)
    void setFieldValue("endTime", endTime)
  }

  const notesCount = reservation.notes?.totalCount || 0

  const activeTables = venue.tables.filter((table: any) => !table.deleted)
  const currentTableIsDeleted = values.tableId && venue.tables.find((t: any) => t.id === values.tableId)?.deleted
  const tablesToShow = currentTableIsDeleted
    ? [...activeTables, venue.tables.find((t: any) => t.id === values.tableId)]
    : activeTables

  const tableCapacityWarning = useMemo(() => {
    if (!values.tableId) return null
    const peopleAmount = normalizePeopleAmount(values.peopleAmount)
    if (peopleAmount == null) return null
    const table = venue.tables.find((t: any) => t.id === values.tableId)
    if (!table) return null

    if (table.minCapacity && peopleAmount < table.minCapacity) {
      return i18n.t("app.settings.venues.venue_layout.table_capacity.min_required", {
        count: table.minCapacity,
      })
    }
    if (table.maxCapacity && peopleAmount > table.maxCapacity) {
      return i18n.t("app.settings.venues.venue_layout.table_capacity.max_exceeded", {
        count: table.maxCapacity,
      })
    }
    return null
  }, [values.tableId, values.peopleAmount, venue.tables])

  const originalCustomerContactId = reservation.customerContact?.id || null
  const isDifferentCustomerSelected =
    selectedCustomerContactId !== null && selectedCustomerContactId !== originalCustomerContactId

  const customerInfoModified =
    values.customerName !== (reservation.customerContact?.name || "") ||
    values.customerEmail !== (reservation.customerContact?.email || "") ||
    values.customerPhone !== (reservation.customerContact?.phone || "")

  const handleCustomerChange = useCallback(
    (data: { name: string; email: string; phone: string }) => {
      void setFieldValue("customerName", data.name)
      void setFieldValue("customerEmail", data.email)
      void setFieldValue("customerPhone", data.phone)
    },
    [setFieldValue],
  )

  const handleCustomerSelected = useCallback(
    (customerId: string | null) => {
      onSelectedCustomerContactIdChange(customerId)
      if (customerId !== null && customerId !== originalCustomerContactId) {
        void setFieldValue("updateAllContacts", false)
      }
    },
    [onSelectedCustomerContactIdChange, originalCustomerContactId, setFieldValue],
  )

  const updateAllContactsDisabled = removed || isDifferentCustomerSelected || !customerInfoModified

  return (
    <div className={STYLES.content.main}>
      <div className={STYLES.leftColumn.container}>
        <div className={STYLES.leftColumn.customerSection}>
          <CustomerAutocomplete
            onChange={handleCustomerChange}
            onCustomerSelected={handleCustomerSelected}
            initialValues={{
              name: reservation.customerContact?.name || "",
              email: reservation.customerContact?.email || "",
              phone: reservation.customerContact?.phone || "",
            }}
            disabled={removed}
          />
          <div className={STYLES.leftColumn.checkboxRow}>
            <Checkbox
              checked={values.updateAllContacts}
              onChange={(e) => setFieldValue("updateAllContacts", e.target.checked)}
              disabled={updateAllContactsDisabled}
              size="small"
            />
            <span className={updateAllContactsDisabled ? "text-gray-400" : ""}>
              {i18n.t("app.edit_reservation.update_in_all_reservations")}
            </span>
          </div>
        </div>

        <Divider className={STYLES.leftColumn.divider} />

        <div className={STYLES.leftColumn.reservationSection}>
          <DateField
            name="date"
            label={i18n.t("app.reservation.labels.date")}
            country={venue.country}
            timezone={venue.timezone}
            disabled={removed}
          />
          <PeopleAmountInput
            name="peopleAmount"
            label={i18n.t("app.reservation.labels.people_amount")}
            disabled={removed}
          />
          <TextField name="tableId" label={i18n.t("app.reservation.labels.table")} select disabled={removed}>
            {tablesToShow.map((table: any) => (
              <MenuItem key={table.id} value={table.id} disabled={table.deleted}>
                {formatTableName(table)} {table.deleted ? "(Deleted)" : null}
              </MenuItem>
            ))}
          </TextField>
          {tableCapacityWarning ? (
            <FormHelperText className="text-orange-500 -mt-2">{tableCapacityWarning}</FormHelperText>
          ) : null}
          <div className={STYLES.leftColumn.row}>
            <TextField name="startTime" label={i18n.t("app.reservation.labels.time")} select disabled={removed}>
              {timeOptions.map(([time, date]: [string, DateTime]) => (
                <MenuItem key={time} value={date.toISO()}>
                  {time}
                </MenuItem>
              ))}
            </TextField>
            <TextField name="endTime" label="" select disabled={removed}>
              {timeOptions.map(([time, date]: [string, DateTime]) => (
                <MenuItem key={time} value={date.toISO()}>
                  {time}
                </MenuItem>
              ))}
            </TextField>
          </div>
          {!removed && <StatusSelector />}
        </div>
      </div>

      <div className={STYLES.rightColumn.container}>
        <div className={STYLES.rightColumn.tabsContainer}>
          <Tabs value={selectedTab} onChange={(_, newValue) => setSelectedTab(newValue)}>
            <Tab label={i18n.t("app.edit_reservation.tabs.venue_plan")} />
            <Tab label={`${i18n.t("app.edit_reservation.tabs.comments")} (${notesCount})`} />
            <Tab label={i18n.t("app.edit_reservation.tabs.activity")} />
          </Tabs>
        </div>
        <div className={`${STYLES.rightColumn.tabContent} overflow-y-auto`}>
          {selectedTab === 0 && (
            <div className={STYLES.rightColumn.mapContainer}>
              <ReservationPlanSelector
                venue={venue}
                venuePlans={venuePlans}
                selectedTableIds={values.tableId ? [values.tableId] : []}
                date={values.date}
                startTime={values.startTime}
                endTime={values.endTime}
                onTableSelect={onTableSelect}
                onTimeSelect={onTimeSelect}
                onPendingChangesChange={onMapPendingChangesChange}
                currentReservationId={reservation.id}
                fullScreen={fullScreen}
                reservationBuffer={reservationBuffer}
              />
            </div>
          )}
          {selectedTab === 1 && <NotesTab venue={venue} reservation={reservation} />}
          {selectedTab === 2 && <ActivitiesTab venue={venue} reservation={reservation} />}
        </div>
      </div>
    </div>
  )
}

const EditForm = ({ venue, reservationId, onClose }) => {
  const [fullScreen, setFullScreen] = useState(false)
  const [mapHasPendingChanges, setMapHasPendingChanges] = useState(false)
  const [selectedCustomerContactId, setSelectedCustomerContactId] = useState<string | null>(null)
  const [updateReservation, { loading: updateLoading }] = useMutation(UpdateReservationDocument, {
    refetchQueries: UPDATE_RESERVATION_REFETCH_QUERIES,
  })
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  const {
    data,
    loading,
    error: loadingError,
  } = useQuery(GetReservationDocument, { variables: { reservationId, slug: venue.slug }, skip: !reservationId })
  const reservation = data?.reservation
  const venuePlans = (data?.venue?.plans || []) as VenuePlanData[]
  const removed = (reservation?.status as string) === STATUS_REMOVED

  const validationSchema = useUpdateReservationValidationSchema({ venue, currentTableId: reservation?.table.id })

  // Reset selectedCustomerContactId when switching reservations to prevent cross-reservation leakage
  useEffect(() => {
    setSelectedCustomerContactId(null)
  }, [reservationId])

  useEffect(() => {
    if (success) {
      const timeout = setTimeout(() => setSuccess(null), 2000)
      return () => clearTimeout(timeout)
    }
  }, [success])

  const onSubmit = async (values: any) => {
    setError(null)

    const peopleAmountValue = normalizePeopleAmount(values.peopleAmount)

    const originalCustomerContactId = reservation?.customerContact?.id || null
    const isDifferentCustomerSelected =
      selectedCustomerContactId !== null && selectedCustomerContactId !== originalCustomerContactId

    const result = await updateReservation({
      variables: {
        reservationId: reservation.id,
        startTime: values.startTime,
        endTime: values.endTime,
        peopleAmount: peopleAmountValue,
        tableId: values.tableId,
        status: values.status,
        customerName: values.customerName,
        customerEmail: values.customerEmail,
        customerPhone: values.customerPhone,
        updateAllContacts: values.updateAllContacts,
        customerContactId: isDifferentCustomerSelected ? selectedCustomerContactId : undefined,
      },
    })

    const resultSuccess = result?.data?.updateReservation?.success

    if (resultSuccess) {
      setSuccess(true)
    } else {
      setError(i18n.t("app.reservation_preview.update_error"))
      console.error("Error updating reservation", result)
    }
  }

  if (!reservation && loading) {
    return (
      <Dialog open onClose={onClose}>
        <div className={STYLES.loading.container}>
          <LoaderCircle fontSize="inherit" className={STYLES.loading.spinner} />
        </div>
      </Dialog>
    )
  }

  if (loadingError || reservation === null) {
    return (
      <Dialog open onClose={onClose}>
        <div className={STYLES.error.container}>
          <Paper className={STYLES.error.paper}>
            <AlertTriangle fontSize="inherit" className={STYLES.error.icon} />
            <div className={STYLES.error.text}>Unable to find the reservation</div>
          </Paper>
        </div>
      </Dialog>
    )
  }

  const formId = `edit-reservation-form-${reservation?.id}`

  return (
    <Dialog
      open={!!reservationId}
      onClose={onClose}
      PaperProps={{
        className: STYLES.dialog.paper,
      }}
      scroll="body"
      fullScreen={fullScreen}
      data-testid="panel-reservations-edit-form"
    >
      {reservation ? (
        <Formik
          initialValues={{
            tableId: reservation.table.id,
            peopleAmount: reservation.peopleAmount,
            date: DateTime.fromISO(reservation.startTime),
            startTime: DateTime.fromISO(reservation.startTime),
            endTime: DateTime.fromISO(reservation.endTime),
            status: reservation.status,
            customerName: reservation.customerContact?.name || "",
            customerEmail: reservation.customerContact?.email || "",
            customerPhone: reservation.customerContact?.phone || "",
            updateAllContacts: false,
            customerContactId: reservation.customerContact?.id || null,
          }}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {(formik) => {
            const currentStatus = formik.values.status as ReservationStatus
            const headerBgColor = RESERVATION_COLORS[currentStatus]?.dark || RESERVATION_COLORS[STATUS_REMOVED].dark

            return (
              <form onSubmit={formik.handleSubmit} id={formId} className="flex flex-col h-full">
                <div className={STYLES.header.base} style={{ backgroundColor: headerBgColor }}>
                  <div className={STYLES.header.title}>
                    Reservation #{reservation.id}
                    {removed ? (
                      <span className={STYLES.header.badge}>{i18n.t("app.reservation.status.removed")}</span>
                    ) : null}
                  </div>
                  <IconButton
                    onClick={() => setFullScreen(!fullScreen)}
                    color="inherit"
                    size="small"
                    data-testid="panel-reservations-edit-form-fullscreen-btn"
                  >
                    {fullScreen ? <Minimize2 className="text-base" /> : <Maximize2 className="text-base" />}
                  </IconButton>
                  <IconButton
                    onClick={onClose}
                    color="inherit"
                    size="small"
                    data-testid="panel-reservations-edit-form-close-btn"
                  >
                    <X className="text-lg" />
                  </IconButton>
                </div>

                {!!error && (
                  <Alert severity="error" className="m-2">
                    {error}
                  </Alert>
                )}

                <EditFormContent
                  venue={venue}
                  reservation={reservation}
                  venuePlans={venuePlans}
                  removed={removed}
                  fullScreen={fullScreen}
                  onMapPendingChangesChange={setMapHasPendingChanges}
                  reservationBuffer={data?.venue?.reservationConfiguration?.reservationBuffer || 0}
                  selectedCustomerContactId={selectedCustomerContactId}
                  onSelectedCustomerContactIdChange={setSelectedCustomerContactId}
                />

                {!removed && (
                  <div className={STYLES.footer.container}>
                    <RemoveButton reservation={reservation} setError={setError} onSuccess={onClose} />

                    <LoadingButton
                      color={success ? "success" : "primary"}
                      loadingPosition="center"
                      loading={updateLoading}
                      type="submit"
                      onClick={formik.submitForm}
                      disabled={mapHasPendingChanges}
                      data-testid="panel-reservations-edit-form-submit-btn"
                    >
                      {success ? <Check /> : i18n.t("app.actions.save")}
                    </LoadingButton>
                  </div>
                )}
              </form>
            )
          }}
        </Formik>
      ) : (
        <div />
      )}
    </Dialog>
  )
}

export default EditForm
